import { Component } from '@theme/component';
import { startViewTransition } from '@theme/utilities';

const STORAGE_KEY = 'maake-facets';

/**
 * "Hide filters / Show filters" button of the collection toolbar (snippets/maake-facets-toggle.liquid).
 *
 * The state lives on `<html data-maake-facets="hidden">`, outside the section, so it survives the
 * morph of the collection after a filter or sort change. It is kept in sessionStorage and restored
 * by an inline script before the grid paints. The element carries `data-skip-subtree-update`, so
 * the morph never resets the button's `aria-expanded`.
 *
 * The switch is animated like Pas Normal Studios (300ms, cubic-bezier(0.4, 0, 0.2, 1)): a view
 * transition (Horizon's `startViewTransition`, type `maake-facets`) moves and resizes the cards in
 * the viewport, each named here for the duration of the transition, and the filter column slides
 * out or in. Horizon's `product-grid` type is not reused: it waits for an idle callback before
 * starting, which delays the click. `maake-facets-animating` on `<html>` scopes the timing and the
 * column's transition name (snippets/maake-facets-toggle.liquid). Without view transitions, on a
 * low-power device or with reduced motion, the switch is instant.
 */
class MaakeFacetsToggle extends Component {
  requiredRefs = ['button'];

  connectedCallback() {
    super.connectedCallback();
    this.#sync();
  }

  async toggle() {
    const hidden = document.documentElement.dataset.maakeFacets !== 'hidden';
    const root = document.documentElement;
    const cards = this.#visibleCards();

    cards.forEach((card) => card.style.setProperty('view-transition-name', `maake-card-${card.dataset.productId}`));
    root.classList.add('maake-facets-animating');

    await startViewTransition(() => this.#apply(hidden), ['maake-facets']);

    root.classList.remove('maake-facets-animating');
    cards.forEach((card) => card.style.removeProperty('view-transition-name'));
  }

  /**
   * Cards in the viewport: only they get a transition name (the others reflow without animation).
   * @returns {HTMLElement[]}
   */
  #visibleCards() {
    return /** @type {HTMLElement[]} */ ([...document.querySelectorAll('.product-grid .product-grid__item')]).filter(
      (card) => {
        const { top, bottom } = card.getBoundingClientRect();
        return bottom > 0 && top < window.innerHeight;
      }
    );
  }

  /**
   * @param {boolean} hidden
   */
  #apply(hidden) {
    if (hidden) {
      document.documentElement.dataset.maakeFacets = 'hidden';
    } else {
      delete document.documentElement.dataset.maakeFacets;
    }

    try {
      if (hidden) {
        sessionStorage.setItem(STORAGE_KEY, 'hidden');
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Storage unavailable (private mode): the state lasts for this page only.
    }

    this.#sync();
  }

  #sync() {
    const hidden = document.documentElement.dataset.maakeFacets === 'hidden';
    this.refs.button.setAttribute('aria-expanded', String(!hidden));
  }
}

if (!customElements.get('maake-facets-toggle')) {
  customElements.define('maake-facets-toggle', MaakeFacetsToggle);
}
